import React from 'react'
import { Link } from 'react-router-dom'
import SeoHead from '../../components/SeoHead'
import { COMPANY, TRAMPOLIN_FAQ, IMAGES } from '../../data/siteData'
import { PAGE_META } from '../../data/metaData'
import { getOptimizedImageUrl } from '../../lib/imageHelper'
import ContactForm from '../../components/ContactForm'
import GallerySection from '../../components/GallerySection'
import FaqAccordion from '../../components/FaqAccordion'

export default function TrampolinePage() {
  const metaTitle = PAGE_META.trampolin.title
  const metaDescription = PAGE_META.trampolin.description

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: TRAMPOLIN_FAQ.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  }

  return (
    <div>
      <SeoHead
        title={metaTitle}
        description={metaDescription}
        schemaData={faqSchema}
        canonicalPath="/nedgravning-af-trampolin"
      />

      {/* Hero */}
      <section className="relative bg-stone-950 text-white overflow-hidden py-16 sm:py-24 border-b border-stone-800">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl(IMAGES.trampolin.hero, { width: 1800, quality: 80 })}
            alt="Færdigmonteret in-ground trampolin i niveau med græsplæne i have"
            className="w-full h-full object-cover opacity-65"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/80 via-stone-950/50 to-stone-950/20"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400 text-xs sm:text-sm font-semibold mb-4">
              <i className="fa-solid fa-circle-nodes" aria-hidden="true"></i>
              <span>Specialist i trampolinnedgravning</span>
            </div>

            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight drop-shadow-md">
              Nedgravning af trampolin, få en harmonisk løsning i haven
            </h1>

            <p className="mt-5 text-base sm:text-xl text-stone-200 leading-relaxed max-w-2xl drop-shadow-sm">
              Vil du have trampolinen integreret naturligt i haven, hjælper <Link to="/" className="text-white font-semibold underline decoration-amber-500 decoration-2 underline-offset-2 hover:text-amber-400 transition-colors">JT Knudsen</Link> med professionel nedgravning af trampolin. Fra vores base i Hjortshøj planlægger vi udgravningen efter trampolinens mål, havens forhold og dine ønsker, så du får en gennemtænkt og pæn løsning.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5">
              <a
                href="#kontakt"
                className="inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-lg bg-[#015205] hover:bg-[#026b07] text-white font-bold text-base shadow-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <span>Få tilbud på trampolinnedgravning</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </a>

              <a
                href={COMPANY.phoneHref}
                className="inline-flex items-center justify-center gap-2.5 px-6 py-4 rounded-lg bg-stone-900 hover:bg-stone-800 text-stone-100 font-bold text-base border border-stone-700 transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-amber-400" aria-hidden="true"></i>
                <span>Ring på {COMPANY.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose In-Ground */}
      <section className="py-14 sm:py-20 bg-stone-50 text-stone-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-5">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-600">
                Roligt haveudtryk & nem adgang
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-stone-900">
                Derfor vælger mange en nedgravet trampolin
              </h2>
              <div className="text-stone-700 space-y-4 text-base sm:text-lg leading-relaxed">
                <p>
                  En nedgravet trampolin virker mindre dominerende og giver haven et mere roligt udtryk. Den lave adgang gør det samtidig nemmere at komme op på trampolinen. Nedgravningen erstatter dog ikke producentens sikkerhedsanvisninger, den anbefalede sikkerhedsafstand eller et eventuelt sikkerhedsnet.
                </p>
                <p>
                  Det færdige resultat afhænger i høj grad af arbejdet under trampolinen. Hullet skal have den rigtige form og dybde, mens der skal tages hensyn til luft, jordbund og vand. Rammen skal desuden have en stabil understøtning, som passer til den valgte model og producentens anvisninger.
                </p>
                <p>
                  Når haven alligevel graves op, vælger mange boligejere at kombinere trampolinprojektet med ny <Link to="/belaegning" className="text-amber-600 hover:text-amber-700 font-semibold underline underline-offset-2">belægning</Link> såsom en fliseterrasse, gangsti eller kantafgrænsning. Har du samtidig planer om et skur, anneks eller støttemur på grunden, udfører vi også udgravning og støbning af <Link to="/fundament" className="text-amber-600 hover:text-amber-700 font-semibold underline underline-offset-2">fundament og sokkel</Link>.
                </p>
              </div>

              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-700 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="fa-solid fa-check text-xs" aria-hidden="true"></i>
                  </div>
                  <span className="text-base text-stone-800 font-medium">
                    En lav adgang, som gør trampolinen lettere at komme op på
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-700 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="fa-solid fa-check text-xs" aria-hidden="true"></i>
                  </div>
                  <span className="text-base text-stone-800 font-medium">
                    Et mere harmonisk udtryk, hvor trampolinen fylder mindre i udsynet
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-700 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="fa-solid fa-check text-xs" aria-hidden="true"></i>
                  </div>
                  <span className="text-base text-stone-800 font-medium">
                    En udgravning, som bliver tilpasset trampolinens form og mål
                  </span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden shadow-lg border border-stone-200">
                <img
                  src={getOptimizedImageUrl(IMAGES.trampolin.work, { width: 800, height: 600 })}
                  alt="Præcis cirkulær udgravning med minigraver til nedgravet trampolin i have"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Model requirements & Placement */}
      <section className="py-14 sm:py-20 bg-stone-100 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="bg-white p-7 sm:p-9 rounded-2xl border border-stone-200 shadow-sm space-y-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/15 text-amber-600 flex items-center justify-center text-xl">
                <i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
                Hvad kræver en trampolin til nedgravning?
              </h3>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Ikke alle trampoliner er beregnet til at blive gravet ned. En trampolin til nedgravning er konstrueret med den nødvendige højde, luftpassage og understøtning. En almindelig fritstående model bør derfor kun nedgraves, hvis producenten udtrykkeligt tillader det og beskriver den korrekte fremgangsmåde.
              </p>
            </div>

            <div className="bg-white p-7 sm:p-9 rounded-2xl border border-stone-200 shadow-sm space-y-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/15 text-amber-600 flex items-center justify-center text-xl">
                <i className="fa-solid fa-compass-drafting" aria-hidden="true"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
                Placeringen afgør mere, end man skulle tro
              </h3>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Placeringen bør være jævn og have god afstand til træer, hegn, bygninger og andre faste genstande. Rødder, nedgravede installationer, adgang for maskiner og risiko for vand i hullet kan også påvirke arbejdet. Vi tager udgangspunkt i haven, trampolinens krav og de praktiske forhold på grunden.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Step by step process */}
      <section className="py-16 sm:py-20 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-wider text-amber-400">
              Gravearbejdet trin for trin
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mt-1">
              Fra opmåling til den færdige udgravning
            </h2>
            <p className="mt-3 text-stone-300 text-base sm:text-lg leading-relaxed">
              Mange omtaler opgaven som trampolin nedgravning. Vi begynder med at få model, mål og ønsker på plads, hvorefter placeringen og udgravningen planlægges. Omfanget aftales tydeligt, herunder hvordan overskudsjorden skal håndteres, og om der er særlige forhold i haven.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-stone-950/70 border border-stone-800 rounded-xl p-6">
              <div className="text-amber-500 font-black text-2xl mb-2">01</div>
              <h4 className="font-bold text-base text-stone-100 mb-2">
                Gennemgang af model
              </h4>
              <p className="text-xs sm:text-sm text-stone-400">
                Model, mål og anvisninger gennemgås inden gravearbejdet.
              </p>
            </div>

            <div className="bg-stone-950/70 border border-stone-800 rounded-xl p-6">
              <div className="text-amber-500 font-black text-2xl mb-2">02</div>
              <h4 className="font-bold text-base text-stone-100 mb-2">
                Afsætning & Sikkerhedszone
              </h4>
              <p className="text-xs sm:text-sm text-stone-400">
                Placering og udgravning afmærkes med plads til sikkerhedszonen.
              </p>
            </div>

            <div className="bg-stone-950/70 border border-stone-800 rounded-xl p-6">
              <div className="text-amber-500 font-black text-2xl mb-2">03</div>
              <h4 className="font-bold text-base text-stone-100 mb-2">
                Præcis udgravning
              </h4>
              <p className="text-xs sm:text-sm text-stone-400">
                Jorden udgraves i den form og dybde, som modellen kræver.
              </p>
            </div>

            <div className="bg-stone-950/70 border border-stone-800 rounded-xl p-6">
              <div className="text-amber-500 font-black text-2xl mb-2">04</div>
              <h4 className="font-bold text-base text-stone-100 mb-2">
                Afslutning & Oprydning
              </h4>
              <p className="text-xs sm:text-sm text-stone-400">
                Afslutningen omkring rammen tilpasses den valgte løsning.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Price factors */}
      <section className="py-16 sm:py-20 bg-stone-50 text-stone-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 space-y-4">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-600">
                Pris & Beregning
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-stone-900">
                Hvad koster det at få trampolinen gravet ned?
              </h2>
              <p className="text-stone-700 text-base leading-relaxed">
                Når du undersøger nedgravning af trampolin pris, er det vigtigt at se på hele opgaven. Prisen afhænger blandt andet af trampolinens størrelse og form, jordens beskaffenhed, adgangen til haven og mængden af overskudsjord. Rødder, sten og behov for håndtering af vand kan også få betydning.
              </p>
              <p className="text-stone-700 text-base leading-relaxed">
                En konkret pris kræver derfor oplysninger om både trampolinen og haven. Når vi kender modellen, målene og adgangsforholdene, kan vi vurdere arbejdets omfang og give et tilbud på den ønskede løsning. På den måde ved du, hvad der er aftalt, inden gravearbejdet bliver sat i gang.
              </p>
            </div>

            <div className="lg:col-span-6">
              <div className="bg-white p-6 sm:p-8 rounded-2xl border border-stone-200 shadow-sm space-y-4">
                <h3 className="font-bold text-lg text-stone-900 border-b border-stone-100 pb-3">
                  Forhold der påvirker din pris:
                </h3>
                <ul className="space-y-3 text-sm sm:text-base text-stone-700">
                  <li className="flex items-start gap-3">
                    <i className="fa-solid fa-circle-check text-amber-500 mt-1" aria-hidden="true"></i>
                    <span>Trampolinens størrelse, form og krav til hullets dybde</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="fa-solid fa-circle-check text-amber-500 mt-1" aria-hidden="true"></i>
                    <span>Adgangsforholdene for maskiner og transport af jord</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="fa-solid fa-circle-check text-amber-500 mt-1" aria-hidden="true"></i>
                    <span>Jordtypen samt forekomsten af sten og større rødder</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="fa-solid fa-circle-check text-amber-500 mt-1" aria-hidden="true"></i>
                    <span>Aftalen om overskudsjord og eventuelle ekstra arbejder</span>
                  </li>
                </ul>

                <div className="pt-4 border-t border-stone-100">
                  <a
                    href="#kontakt"
                    className="inline-flex items-center gap-2 text-sm font-bold text-amber-600 hover:text-amber-700"
                  >
                    <span>Få beregnet en skarp pris på din have</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Local coverage snippet */}
      <section className="py-12 bg-stone-100 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-wider text-amber-600">
              Lokal tilstedeværelse
            </span>
            <h3 className="text-xl sm:text-2xl font-bold text-stone-900 mt-1">
              Lokal hjælp i Østjylland og på Djursland
            </h3>
            <p className="mt-2 text-stone-700 text-sm sm:text-base leading-relaxed">
              JT Knudsen holder til i Hjortshøj og kører både i Østjylland og på Djursland. Vi hjælper blandt andet med nedgravning af trampolin i Aarhus, Grenaa, Hornslet, Hinnerup og Egå. Den lokale placering gør det nemt at tage en direkte dialog om haven, adgangsforholdene og dine ønsker.
            </p>
          </div>
        </div>
      </section>

      {/* Dynamic Gallery for Trampolin */}
      <section className="py-16 bg-stone-50 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <GallerySection
            galleryKey="nedgravning-af-trampolin"
            title="Billeder af trampolinnedgravning"
            subtitle="Se eksempler på udgravning og færdigmonterede trampoliner i niveau med græsplænen"
          />
        </div>
      </section>

      {/* FAQ Accordion */}
      <FaqAccordion
        items={TRAMPOLIN_FAQ}
        title="Spørgsmål om nedgravning af trampolin"
        subtitle="Her finder du svar på alt det praktiske om mål, udgravning, sikkerhed og priser"
      />

      {/* Contact Form Section */}
      <section className="py-16 sm:py-24 bg-stone-950 text-white border-t border-stone-800" id="kontakt">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 space-y-6">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-400">
                Få et uforpligtende tilbud
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                Klar til at få trampolinen ned i haven?
              </h2>
              <p className="text-stone-300 text-base sm:text-lg leading-relaxed">
                Kontakt JT Knudsen og fortæl, hvilken trampolin du har valgt, hvor den skal placeres, og hvordan adgangen til haven ser ud. Du må også gerne have modellens mål og billeder af området klar. Så kan vi tage en praktisk snak om arbejdet og finde en løsning, der passer til både trampolinen og haven.
              </p>

              <div className="p-5 rounded-xl bg-stone-900 border border-stone-800 space-y-3 text-sm">
                <div className="font-semibold text-stone-200">Hurtig kontakt til Jonas:</div>
                <div className="flex items-center gap-2 text-stone-300">
                  <i className="fa-solid fa-phone text-amber-400" aria-hidden="true"></i>
                  <a href={COMPANY.phoneHref} className="hover:text-amber-400 font-bold whitespace-nowrap">
                    {COMPANY.phone}
                  </a>
                </div>
                <div className="flex items-center gap-2 text-stone-300">
                  <i className="fa-solid fa-envelope text-amber-400" aria-hidden="true"></i>
                  <a href={`mailto:${COMPANY.email}`} className="hover:text-amber-400 whitespace-nowrap break-keep">
                    {COMPANY.email}
                  </a>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ContactForm defaultService="Nedgravning af trampolin" />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
